'use client'

import { useMemo } from 'react'
import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils/cn'

import { seededRand } from './format-time'

interface WaveBarsProps {
  seed?: number
  bars?: number
  /** 播放进度 0-1，已播放部分高亮 */
  progress?: number
  height?: number
  /** 是否正在播放（已播放的柱条有轻微缩放） */
  active?: boolean
  /** 是否正在播放：开启后所有柱条按 seed 基础高度跳动 */
  playing?: boolean
}

/**
 * 内联式静态波形条
 * 基于 seed 生成稳定形态，progress 控制已播放高亮
 * playing=true 时柱条按 seed 基础高度做循环跳动，模拟真实音频
 */
export const WaveBars = ({
  seed = 1,
  bars = 64,
  progress = 0,
  height = 28,
  active = false,
  playing = false,
}: WaveBarsProps) => {
  const rand = useMemo(() => {
    const r = seededRand(seed)
    return Array.from({ length: bars }, () => 0.18 + r() * 0.82)
  }, [seed, bars])

  return (
    <div
      className="flex flex-1 min-w-0 items-center gap-0.5 h-[var(--wb-h)]"
      style={{ '--wb-h': `${height}px` } as CSSProperties}
    >
      {rand.map((v, i) => {
        const played = i / bars < progress
        return (
          <div
            key={i}
            className={cn(
              'flex-1 min-w-px rounded-[2px] h-[var(--wb-bh)] origin-bottom',
              played ? 'bg-white/[0.9]' : 'bg-white/[0.22]',
              playing
                ? 'animate-[voice-wave-bounce_1.2s_ease-in-out_var(--wb-d)_infinite]'
                : 'transition-[background,transform] duration-200',
              !playing && active && played ? 'scale-y-105' : 'scale-y-100'
            )}
            style={
              {
                '--wb-bh': `${v * 100}%`,
                '--wb-d': `${(i % 16) * 0.06}s`,
              } as CSSProperties
            }
          />
        )
      })}
    </div>
  )
}
